import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {APIv1} from "../../../api";
import {message, Modal, Tag} from "antd";
import {MessageSquareText, Wallet} from "lucide-react";
import {useTranslation} from "react-i18next";
import Logs from "../../Logs/Logs";
import {extractDateBySecond, isBoolean} from "../../../utils";
import {Sms} from "../../index";
import Device from "../../Devices/Device";
import {renderCompanyTypeTag} from "../../../utils/company/company_type";
import {getColorBySoftwareName} from "../../../utils/company/software_type";
import ShowUserPicture from "../../../components/UserProfileCom/ShowUserPicture/ShowUserPicture";
import OrderHistoryRange from "../../../components/UserProfileCom/OrderHistoryRange/OrderHistoryRange";
import ExpandableSection from "../../../components/Detail/ExpandableSection/ExpandableSection";
import "../../../styles/BaseDetailStyle.scss";
import {playFeedbackWave} from "../../../utils/feedbackWave";

const TELEGRAM_NOTIFY_TYPES = [
    {value: "log", labelKey: "errorLog"},
    {value: "warning", labelKey: "warning"},
    {value: "high_temp", labelKey: "highTemp"},
];

const COMPANY_STATUS_LABEL_KEYS = {
    ACTIVE: "active",
    DISCONNECTED: "disconnected",
    INACTIVE: "inactive",
    CONTROL_OFF: "controlOff",
};

const CompanyDetail = () => {
    const {id} = useParams();
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [company, setCompany] = useState({});
    const [companyImages, setCompanyImages] = useState({});
    const [userData, setUserData] = useState({});
    const [isUserStaff, setIsUserStaff] = useState({});
    const [isModalVisible, setIsModalVisible] = useState(false); // For controlling the modal visibility

    useEffect(() => {
        const items = JSON.parse(localStorage.getItem('user'));
        if (items) {
            setUserData(items);
        }
    }, []);

    useEffect(() => {
        const items = JSON.parse(localStorage.getItem('user'));
        if (items) {
            setIsUserStaff(items.data.is_admin);
            setUserData(items);
        }
    }, [userData.token]);

    useEffect(() => {
        const fetchCompanyDetail = async () => {
            try {
                const userToken = userData.token;

                // 1️⃣ First, fetch company details
                const response = await APIv1.get(`/company/detail/${id}`, {
                    headers: {Authorization: `Token ${userData.token}`},
                });
                const companyData = response.data;
                setCompany(companyData);

                // 2️⃣ Then, use companyData.inn to fetch images
                const imagesResponse = await APIv1.get(
                    `/company/profile/images/contents/${companyData.inn}`,
                    {
                        headers: {Authorization: `Token ${userToken}`},
                    }
                );

                setCompanyImages({
                    billingLogo: imagesResponse.data.data.billing_logo,
                    logo: imagesResponse.data.data.logo,
                    banner: imagesResponse.data.data.banner
                });
            } catch (err) {
                console.error('Something went wrong:', err);
            }
        };

        fetchCompanyDetail();
    }, [id, userData.token]);

    if (!company) {
        return <div>Company not found</div>;
    }

    const onSendSelectedCompaniesToDelete = async () => {
        const response = await APIv1.delete(`/company/delete/${company.inn}/`, {
            headers: {
                Authorization: `Token ${userData.token}`,
            },
        });

        if (response.status === 200) {
            playFeedbackWave('success');
            message.success(t("pages.companies.deleteItem.messages.success1"));
            navigate(-1);
        } else {
            playFeedbackWave('failure');
            message.error(t("pages.companies.deleteItem.messages.error1"));
        }
    };

    const handleCreateCompanyNavigate = () => {
        navigate(`/create_company/${company.id}`);
    };

    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const handleOk = () => {
        onSendSelectedCompaniesToDelete();
        setIsModalVisible(false);
    };

    const renderListItem = (label, value, isPayment = false) => (
        <li key={label} className="detail-view__item">
            <span className="detail-view__label">{label}:</span>
            {isPayment ? (
                <div className="detail-view__value-group">
                    {/*<Button*/}
                    {/*    onClick={() => handleRedirect(label)}*/}
                    {/*    className="detail-view__redirect-button"*/}
                    {/*>*/}
                    {/*    More*/}
                    {/*</Button>*/}
                    <span
                        className={`detail-view__tag ${value ? 'detail-view__tag--success' : 'detail-view__tag--error'}`}>
                        {value ? `${t("common.detailPages.access")}` : `${t("common.detailPages.decline")}`}
                    </span>
                </div>
            ) : isBoolean(value) ? (
                <span className={`detail-view__tag ${value ? 'detail-view__tag--success' : 'detail-view__tag--error'}`}>
                    {value ? `${t("common.detailPages.access")}` : `${t("common.detailPages.decline")}`}
                </span>
            ) : (
                <span className="detail-view__value">{value}</span>
            )}
        </li>
    );

    const renderHeaderBadge = (label, value, modifier = 'default') => {
        if (!value) return null;

        return (
            <span className={`detail-view__meta-badge detail-view__meta-badge--${modifier}`}>
                <span className="detail-view__meta-badge-label">{label}</span>
                <span className="detail-view__meta-badge-value">{value}</span>
            </span>
        );
    };

    const renderHeaderBadgeGroup = (label, values, modifier = 'default') => {
        if (!values || values.length === 0) return null;

        return (
            <span className={`detail-view__meta-badge detail-view__meta-badge--${modifier}`}>
                <span className="detail-view__meta-badge-label">{label}</span>
                <span className="detail-view__meta-badge-value">
                    {values.join(' | ')}
                </span>
            </span>
        );
    };

    const renderTelegramGroups = () => {
        const telegramGroups = Array.isArray(company.telegram_groups) ? company.telegram_groups : [];

        if (telegramGroups.length === 0) {
            return (
                <span className="detail-view__empty-value">
                    {t("common.detailPages.empty")}
                </span>
            );
        }

        return (
            <div className="detail-view__telegram-groups">
                {telegramGroups.map((group) => (
                    <div key={group.id || group.group_id} className="detail-view__telegram-group">
                        <div className="detail-view__telegram-group-id-wrap">
                            <span className="detail-view__telegram-group-label">
                                {t("pages.companies.detailColumns.container4.groupId")}
                            </span>
                            <span className="detail-view__telegram-group-id">
                                {group.group_id}
                            </span>
                        </div>
                        <div className="detail-view__telegram-tags">
                            {TELEGRAM_NOTIFY_TYPES.map((notifyType) => {
                                const isEnabled = (group.notify_types || []).includes(notifyType.value);

                                return (
                                    <Tag
                                        key={notifyType.value}
                                        bordered={false}
                                        className={`detail-view__tag ${
                                            isEnabled
                                                ? 'detail-view__tag--success'
                                                : 'detail-view__tag--error'
                                        }`}
                                    >
                                        {t(`pages.companies.detailColumns.container4.notifyTypes.${notifyType.labelKey}`)}
                                    </Tag>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <section className="detail-view">
            <div className="detail-view__header detail-view__header--company">
                <div className="detail-view__company-heading-row">
                    <h1 className="detail-view__company-title">{company.name}</h1>
                    <div className="detail-view__action-buttons">
                        {isUserStaff && (
                            <button
                                className="detail-view__action-button detail-view__action-button--delete"
                                onClick={showModal}
                            >
                                {t("pages.companies.detailColumns.button1")}
                            </button>
                        )}

                        <button
                            className="detail-view__action-button detail-view__action-button--secondary"
                            onClick={() => handleCreateCompanyNavigate()}
                        >
                            {t("common.edit")}
                        </button>

                        <button
                            className="detail-view__action-button detail-view__action-button--secondary"
                            onClick={() => navigate(-1)}
                        >
                            {t("pages.companies.detailColumns.button3")}
                        </button>
                    </div>
                </div>
                <div className="detail-view__company-meta">
                    {renderHeaderBadge('ID', company.id)}
                    {renderHeaderBadge('INN', company.inn)}
                    {renderHeaderBadge('Public name', company.public_name)}
                    {renderHeaderBadge('Type', company.company_type, 'type')}
                    {renderHeaderBadgeGroup('Software', company.software, 'software')}
                </div>
            </div>

            <div style={{marginBottom: 25}}>
                <h2 className="related-device-title">{t("pages.companies.detailColumns.relatedSection1.title")}</h2>
                {company.inn && (
                    <Device
                        defaultPageSize={10}
                        companyInn={company.inn}
                    />
                )}
            </div>

            <div className="detail-view__content detail-view__content--company-summary">
                <div className="detail-view__section detail-view__section--company-base">
                    <h2 className="detail-view__section-title">{t("pages.companies.detailColumns.container1.title")}</h2>
                    <ul className="detail-view__list">
                        {renderListItem(`${t("common.inn")}`, company.inn)}
                        {renderListItem(
                            `${t("common.status")}`,
                            t(`common.${COMPANY_STATUS_LABEL_KEYS[company.status] || "notAvailable"}`)
                        )}
                        {renderListItem(`${t("pages.companies.detailColumns.container1.row5")}`, company.public_name)}
                        {renderListItem(`${t("pages.companies.detailColumns.container1.row1")}`, company.address)}
                        {renderListItem(`${t("pages.companies.detailColumns.container1.row2")}`, company.phone_number)}
                        {renderListItem(`${t("pages.companies.detailColumns.container1.row6")}`, company?.company_type && renderCompanyTypeTag(company.company_type))}
                        {renderListItem(
                            `${t("pages.companies.detailColumns.container1.row7")}`,
                            company.software?.map((item, index) => (
                                <Tag key={index} color={getColorBySoftwareName(item)}>
                                    {item}
                                </Tag>
                            ))
                        )}

                        {renderListItem(`${t("pages.companies.detailColumns.container1.row3")}`, extractDateBySecond(company.start_date))}
                        {renderListItem(`${t("pages.companies.detailColumns.container1.row4")}`, extractDateBySecond(company.end_date))}
                        {renderListItem(
                            `${t("pages.companies.detailColumns.container1.row8")}`,
                            <OrderHistoryRange value={company.order_history_range} compact/>
                        )}
                    </ul>
                </div>

                <div className="detail-view__company-side-sections">
                    <div className="detail-view__section detail-view__section--compact">
                        <h2 className="detail-view__section-title">{t("pages.companies.detailColumns.container2.title")}</h2>
                        <ul className="detail-view__list">
                            {renderListItem(`${t("common.providers.click")}`, company.click, true)}
                            {renderListItem(`${t("common.providers.payme")}`, company.pay_me, true)}
                            {renderListItem(`${t("common.providers.uzum")}`, company.apelsin, true)}
                            {renderListItem(`${t("common.providers.anor")}`, company.anor, true)}
                        </ul>
                    </div>

                    <div className="detail-view__section detail-view__section--compact">
                        <h2 className="detail-view__section-title">{t("pages.companies.detailColumns.container4.title")}</h2>
                        {renderTelegramGroups()}
                    </div>

                    <div className="detail-view__section detail-view__section--compact">
                        <h2 className="detail-view__section-title">{t("pages.companies.detailColumns.container3.title")}</h2>
                        <ul className="detail-view__list">
                            {renderListItem(`${t("pages.companies.detailColumns.container3.row1")}`, company.send_sms)}
                            {renderListItem(`${t("pages.companies.detailColumns.container3.row2")}`, company.count_sent_sms)}
                            {renderListItem(`${t("pages.companies.detailColumns.container3.row3")}`, company.last_month_sms_count)}
                        </ul>

                        {/*<h2 className="detail-view__section-title">{t("pages.companies.detailColumns.container3.title")}</h2>*/}
                        {/*<div className="profile-picture">*/}
                        {/*    <ShowUserPicture*/}
                        {/*        data={{*/}
                        {/*            billingLogo: companyImages.billingLogo,*/}
                        {/*            logo: companyImages.logo,*/}
                        {/*            banner: companyImages.banner,*/}
                        {/*        }}*/}
                        {/*        inn={company.inn}*/}
                        {/*        userToken={userData.token}*/}
                        {/*        isEditing={false}*/}
                        {/*    />*/}
                        {/*</div>*/}
                    </div>
                </div>
            </div>
            <ExpandableSection
                title={t("pages.companies.detailColumns.relatedSection2.title")}
                icon={<Wallet size={16}/>}
            >
                <Logs
                    defaultPaginationSize={10}
                    companyInn={company.inn}
                />
            </ExpandableSection>

            <ExpandableSection
                title={t("pages.companies.detailColumns.relatedSection3.title")}
                icon={<MessageSquareText size={16}/>}
            >
                <Sms
                    defaultPaginationSize={10}
                    companyInn={company.inn}
                />
            </ExpandableSection>

            <Modal
                title={t("pages.companies.deleteItem.header")}
                visible={isModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
                okText={t("pages.companies.deleteItem.button1")}
                cancelText={t("pages.companies.deleteItem.button2")}
                okType="danger"
            >
                <p>{t("pages.companies.deleteItem.title")}: <strong>{company.name}</strong>?</p>
            </Modal>
        </section>
    );
};

export default CompanyDetail;
